import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import styled from "styled-components";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
} from "chart.js";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { collection, doc, setDoc, getDoc } from "firebase/firestore";
import Stats from "./Stats";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip
);

const SpendingsAndIncomeTracker = () => {
  const { currentUser } = useAuth();
  const [data, setData] = useState([]); // Starting with an empty array
  const [incomeInput, setIncomeInput] = useState("");
  const [spendingInput, setSpendingInput] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (currentUser) {
        setLoading(true);
        const userDoc = doc(
          db,
          "users",
          currentUser.uid,
          "incomeTracker",
          "data"
        );
        const docSnap = await getDoc(userDoc);

        if (docSnap.exists()) {
          const savedData = docSnap.data();
          setData(savedData.history || []);
        }
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const handleAddIncome = async () => {
    if (!isNaN(Number(incomeInput)) && incomeInput !== "") {
      const lastValue = data[data.length - 1]?.value || 0;
      const newValue = lastValue + Number(incomeInput);
      const newData = [
        ...data,
        { value: newValue, date: new Date().toISOString() },
      ];
      setData(newData); // Update data using default setter

      // Save data after updating state
      if (currentUser) {
        const userDoc = doc(
          db,
          "users",
          currentUser.uid,
          "incomeTracker",
          "data"
        );
        await setDoc(userDoc, { history: newData });
      }
    }
  };

  const handleAddSpending = async () => {
    if (!isNaN(Number(spendingInput)) && spendingInput !== "") {
      const lastValue = data[data.length - 1]?.value || 0;
      const newValue = lastValue - Number(spendingInput);
      const newData = [
        ...data,
        { value: newValue, date: new Date().toISOString() },
      ];
      setData(newData); // Update data using default setter

      // Save data after updating state
      if (currentUser) {
        const userDoc = doc(
          db,
          "users",
          currentUser.uid,
          "incomeTracker",
          "data"
        );
        await setDoc(userDoc, { history: newData });
      }
    }
  };

  const handleSaveData = async () => {
    if (currentUser) {
      const userDoc = doc(
        db,
        "users",
        currentUser.uid,
        "incomeTracker",
        "data"
      );
      await setDoc(userDoc, { history: data });
      alert("Data saved successfully!");
    }
  };

  // Calculate total income, spending, and current value
  const calculateTotals = (data) => {
    let totalIncome = 0;
    let totalSpending = 0;
    let previousValue = 0;

    for (const entry of data) {
      const difference = entry.value - previousValue;
      if (difference > 0) {
        totalIncome += difference;
      } else if (difference < 0) {
        totalSpending += difference; // Spending is a negative value
      }
      previousValue = entry.value;
    }
    return { totalIncome, totalSpending };
  };

  const { totalIncome, totalSpending } = calculateTotals(data);
  // const totalSpending = data.reduce(
  //   (acc, entry) => (entry.value < 0 ? acc + entry.value : acc),
  //   0
  // );
  const currentTotal = data[data.length - 1]?.value || 0;

  const chartData = {
    labels: data.map((entry, index) => entry.date || `Point ${index + 1}`),
    datasets: [
      {
        label: "Income/Spending Tracker",
        data: data.map((entry) => entry.value),
        borderColor: "rgba(75,192,192,1)",
        backgroundColor: "rgba(75,192,192,0.2)",
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Dates",
        },
      },
      y: {
        title: {
          display: true,
          text: "Value",
        },
      },
    },
  };

  return (
    <TrackerContainer>
      <Title>Dynamic Chart Tracker</Title>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <StatsContainer>
            <Stat type="income">Total Income: ${totalIncome.toFixed(2)}</Stat>
            <Stat type="spending">
              Total Spending: ${totalSpending.toFixed(2)}
            </Stat>
            <Stat type="total">Current Total: ${currentTotal.toFixed(2)}</Stat>
          </StatsContainer>
          <InputContainer>
            <InputGroup>
              <NumberInput
                type="number"
                value={incomeInput}
                onChange={(e) => setIncomeInput(e.target.value)}
                placeholder="Enter income"
              />
              <Button onClick={handleAddIncome}>Add Income</Button>
            </InputGroup>
            <InputGroup>
              <NumberInput
                type="number"
                value={spendingInput}
                onChange={(e) => setSpendingInput(e.target.value)}
                placeholder="Enter spendings"
              />
              <Button onClick={handleAddSpending}>Add Spending</Button>
            </InputGroup>
          </InputContainer>
          <Line data={chartData} options={chartOptions} />
          {/* <Button onClick={handleSaveData} style={{ marginTop: "20px" }}>
            Save Data
          </Button> */}
          {/* <Stats
            totalIncome={totalIncome || 0}
            totalSpending={totalSpending || 0}
            currentTotal={currentTotal || 0}
          /> */}
        </>
      )}
    </TrackerContainer>
  );
};

const TrackerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  width: 100%;
  margin: 20px auto;
  background: #171c29;
  color: white;
  border-radius: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  @media (max-width: 1440px) {
    width: 48%;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  margin-bottom: 20px;
`;

const StatsContainer = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 10px;
  text-align: left;
  font-size: 16px;
  width: 100%;
`;

const Stat = styled.p`
  margin: 0;
  padding: 5px 10px;
  border-left: 4px solid
    ${(props) =>
      props.type === "income"
        ? "green"
        : props.type === "spending"
        ? "red"
        : "blue"};
  /* color: ${(props) =>
    props.type === "income"
      ? "green"
      : props.type === "spending"
      ? "red"
      : "blue"}; */
`;

const InputContainer = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  width: 100%;
  @media (max-width: 2200px) {
    flex-direction: column;
    justify-content: center;
  }
`;

const InputGroup = styled.div`
  display: flex;
  align-items: center;
  @media (max-width: 2200px) {
    width: 100%;
  }
`;

const NumberInput = styled.input`
  border: none;
  color: white;
  font-family: "Comfortaa";
  background: #20273a;
  padding: 15px;
  font-size: 16px;
  border-radius: 10px 0 0 10px;
  @media (max-width: 2200px) {
    width: 70%;
  }
`;

const Button = styled.button`
  padding: 14px 10px;
  font-size: 16px;
  color: #313131;
  background-color: #ffffff;
  border: none;
  cursor: pointer;
  font-family: "Comfortaa";
  border-radius: 0 10px 10px 0;
  transition: 0.4s;
  &:hover {
    background-color: #cbdef1;
  }
  @media (max-width: 2200px) {
    width: 25%;
  }
`;

export default SpendingsAndIncomeTracker;
